import { Button, Col, Collapse, DatePicker, Form, Grid, Input, Popconfirm, Radio, Row, Select, theme, Typography } from 'antd';
import { Content } from 'antd/es/layout/layout';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { APOLLO_DATE_FORMAT, DATE_FORMAT, FileUploadSpec } from '../../../../../../../../constants';
import {
  FileInfo,
  IdentityVerificationCaseV1CaseDetailsApplicationInput,
  IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails
} from '../../../../../../../../generated';
import { Country } from '../../../../../../../../types/country';
import { IdCaseFileUploadButtonWithPreviewContainer } from '../../../../../../../shared/file-upload-button-with-preview/id-case-file-upload-button-with-preview.container';
import { AzureBlobResponse } from '../../../../../../../shared/file-upload/azure-upload';
import { PaymentContainer } from '../../../../../components/payment.container';
import { CertificationByApplicantLegalDisclaimer } from '../../../../../components/certification-by-applicant-legal-disclaimer';
import utc from 'dayjs/plugin/utc';
import { ExclamationCircleOutlined } from '@ant-design/icons/lib/icons';
dayjs.extend(utc);

interface DraftProps {
  applicationPassportDetails: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails;
  countryData: Country[];
  onSubmit: (values: IdentityVerificationCaseV1CaseDetailsApplicationInput) => void;
  caseId: string;
  fetchFilesData: () => any;
  isAttestationComplete: boolean;
  onUploadSuccess: (response: AzureBlobResponse, fieldName: string) => Promise<void>;
  onWithdrawRequest: () => Promise<void>;
  formLoading: boolean;
  serviceFee: number;
}
export const Draft: React.FC<DraftProps> = (props) => {
  const [isExpirationButtonVisible, setIsExpirationButtonVisible] = useState<boolean>(props.applicationPassportDetails?.isPassportExpirationDateVisible ?? false);
  const [isTranslationButtonVisible, setIsTranslationButtonVisible] = useState<boolean>(props.applicationPassportDetails?.isPassportInLatinCharacters ?? false);

  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();
  const [form] = Form.useForm();

  const fetchFiles = async () => {
    const fileData: FileInfo[] = await props.fetchFilesData();
    return fileData;
  };

  useEffect(() => {
    const fetchAndLogFiles = async () => {
      try {
        const fileData = await fetchFiles();
        fileData.forEach((file: FileInfo) => {
          if (file.name.includes(FileUploadSpec.Passport.fileName)) {
            form.setFieldValue(['passportUpload'], 'uploaded');
          }
          if (file.name.includes(FileUploadSpec.PassportExpirationPage.fileName)) {
            form.setFieldValue(['passportExpirationUpload'], 'uploaded');
          }
          if (file.name.includes(FileUploadSpec.PassportTranslation.fileName)) {
            form.setFieldValue(['passportTranslationUpload'], 'uploaded');
          }
          if (file.name.includes(FileUploadSpec.Photo.fileName)) {
            form.setFieldValue(['photo'], 'uploaded');
          }
        });
      } catch (error) {
        console.error('Error fetching file data:', error);
      }
    };

    fetchAndLogFiles();
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      caseDetails: {
        application: {
          passportDetails: {
            isPassportExpirationDateVisible: props.applicationPassportDetails?.isPassportExpirationDateVisible || false,
            isPassportInLatinCharacters: props.applicationPassportDetails?.isPassportInLatinCharacters || false,
            ...props.applicationPassportDetails,
            passportIssuedAt: props.applicationPassportDetails?.passportIssuedAt ? dayjs(props.applicationPassportDetails.passportIssuedAt) : null,
            passportExpiresAt: props.applicationPassportDetails?.passportExpiresAt ? dayjs(props.applicationPassportDetails.passportExpiresAt) : null
          },
          isAttestationComplete: props.isAttestationComplete
        }
      }
    });
  }, []);

  const warningItems = [
    {
      key: '1',
      label: (
        <p>
          <ExclamationCircleOutlined /> Important Information
        </p>
      ),
      children: (
        <Typography.Text style={{ fontSize: '13px' }}>
          Before uploading, be sure to read the Passport information on the <a href="https://www.ecfmg.org/cvhp/" target="_blank" rel="noopener noreferrer">instructions page</a> for details and requirements.
        </Typography.Text>
      )
    },
  ]

  const onSubmit = async () => {
    form
      .validateFields()
      .then(async (values) => {
        form.submit();
        console.log('values', values);
        const formattedValues: IdentityVerificationCaseV1CaseDetailsApplicationInput = {
          passportDetails: {
            passportNumber: values.caseDetails.application.passportDetails.passportNumber,
            passportCountry: values.caseDetails.application.passportDetails.passportCountry,
            passportIssuedAt: values.caseDetails.application.passportDetails.passportIssuedAt.format(APOLLO_DATE_FORMAT),
            passportExpiresAt: values.caseDetails.application.passportDetails.passportExpiresAt.format(APOLLO_DATE_FORMAT),
            isPassportInLatinCharacters: JSON.parse(values.caseDetails.application.passportDetails.isPassportInLatinCharacters),
            isPassportExpirationDateVisible: JSON.parse(values.caseDetails.application.passportDetails.isPassportExpirationDateVisible)
          },
          isAttestationComplete: values.informationAuthorization && values.certificationAgreement && values.termAcknowledgement && values.understandTerms
        };
        props.onSubmit(formattedValues);
      })

      .catch((error) => {
        console.log('Error', error);
      });
  };

  const screen = Grid.useBreakpoint();
  const isMobile = screen.xs;

  if (props.formLoading) {
    return (
      <Row justify={'center'} style={{ height: '100%', alignItems: 'center' }}>
        <Typography.Title>Processing... Please wait</Typography.Title>
      </Row>
    );
  }
  return (
    <Content
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <div
        style={{
          background: colorBgContainer,
          width: isMobile ? '100%' : '70%',
          minWidth: 280,
          padding: isMobile ? '48px 0px 0px 0px' : '48px',
          borderRadius: borderRadiusLG,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'baseline'
        }}
      >
        <h2>Passport Details</h2>
        <Form
          form={form}
          style={{
            width: isMobile ? '100%' : '70%'
          }}
          initialValues={{
            caseDetails: {
              application: {
                passportDetails: {
                  ...props.applicationPassportDetails,
                  passportIssuedAt: props.applicationPassportDetails?.passportIssuedAt ? dayjs(props.applicationPassportDetails?.passportIssuedAt).utc() : null,
                  passportExpiresAt: props.applicationPassportDetails?.passportExpiresAt ? dayjs(props.applicationPassportDetails?.passportExpiresAt).utc() : null
                },
                isAttestationComplete: props.isAttestationComplete
              }
            }
          }}
          layout="vertical"
        >
          <Form.Item
            name={['caseDetails', 'application', 'passportDetails', 'passportCountry']}
            label="Country that Issued Passport"
            rules={[{ required: true, message: 'Please select a country' }]}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Select style={{ width: '100%' }} placeholder="Select Country" showSearch optionFilterProp="children">
              {props.countryData?.map((item: Country) => (
                <Select.Option key={item.countryCode} value={item.countryCode}>
                  {item.countryName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, flexDirection: isMobile ? 'column' : 'row' }}>
            <Form.Item
              name={['caseDetails', 'application', 'passportDetails', 'passportIssuedAt']}
              valuePropName="value"
              label="Passport Issue Date"
              validateTrigger={['onChange', 'onBlur']}
              rules={[
                {
                  required: true,
                  message: 'Please select passport issue date'
                }
              ]}
              getValueProps={(value) => ({
                value: value ? dayjs(value).utc() : ''
              })}
            >
              <DatePicker
                value={props?.applicationPassportDetails?.passportIssuedAt ? dayjs(props?.applicationPassportDetails?.passportIssuedAt).utc() : dayjs().utc()}
                format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                style={{ width: '100%' }}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>
            <Form.Item
              label="Passport Expiration Date"
              valuePropName="value"
              name={['caseDetails', 'application', 'passportDetails', 'passportExpiresAt']}
              rules={[
                {
                  required: true,
                  message: 'Please select passport expiration date'
                }
              ]}
              validateTrigger={['onChange', 'onBlur']}
              getValueProps={(value) => ({
                value: value ? dayjs(value).utc() : ''
              })}
            >
              <DatePicker
                format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                minDate={form.getFieldValue(['passportExpiresAt'])}
                value={props?.applicationPassportDetails?.passportExpiresAt ? dayjs(props?.applicationPassportDetails?.passportExpiresAt).utc() : ''}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
          <Form.Item
            label="Passport Number"
            name={['caseDetails', 'application', 'passportDetails', 'passportNumber']}
            rules={[
              { required: true, message: 'Please enter passport number' },
              {
                validator: (_, value) => (/^[A-Z]+$/.test(value) ? Promise.reject(new Error('Invalid Passport Number')) : Promise.resolve())
              }
            ]}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Input
              maxLength={9}
              style={{ width: '100%' }}
              placeholder="Passport Number"
              onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase())}
            />
          </Form.Item>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Form.Item
              name={['passportUpload']}
              rules={[
                {
                  required: true,
                  message: 'Please upload the passport'
                }
              ]}
            >
              <Typography.Title level={5}>
                Passport Upload
              </Typography.Title>
              <Collapse size="small" ghost items={[warningItems[0]]} />
              <IdCaseFileUploadButtonWithPreviewContainer
                buttonText={'Upload Passport'}
                permittedContentTypes={FileUploadSpec.Passport.permittedContentTypes}
                permittedExtensions={FileUploadSpec.Passport.permittedExtensions}
                caseId={props.caseId}
                fileName={FileUploadSpec.Passport.fileName}
                onUploadSuccess={(response) => {
                  form.setFieldsValue({ passportUpload: 'uploaded' });
                  return props.onUploadSuccess(response, FileUploadSpec.Passport.fieldName);
                }}
                isShowFileName={false}
                cropperProps={{ aspect: 3 / 4 }}
              />
            </Form.Item>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
              <Form.Item
                getValueProps={(value) => ({
                  value: !!value
                })}
                style={{ marginTop: '25px' }}
                label="Expiration Date Visible in Scan?"
                valuePropName="value"
                name={['caseDetails', 'application', 'passportDetails', 'isPassportExpirationDateVisible']}
                rules={[
                  {
                    required: true
                  }
                ]}
                validateTrigger={['onChange', 'onBlur']}
                labelCol={{ span: 24 }}
              >
                <Radio.Group
                  style={{ marginLeft: '8px' }}
                  onChange={(e) => {
                    setIsExpirationButtonVisible(e.target.value);
                    form.setFieldsValue({
                      'caseDetails.application.passportDetails.isPassportExpirationDateVisible': e.target.value
                    });
                  }}
                  value={isExpirationButtonVisible}
                  defaultValue={props.applicationPassportDetails?.isPassportExpirationDateVisible}
                >
                  <Radio value={false}>No</Radio>
                  <Radio value={true}>Yes</Radio>
                </Radio.Group>
              </Form.Item>
              {isMobile && !isExpirationButtonVisible && (
                <Form.Item
                  name="passportExpirationUpload"
                  rules={[
                    {
                      required: true,
                      message: 'Please upload the passport expiration page'
                    }
                  ]}
                >
                  <IdCaseFileUploadButtonWithPreviewContainer
                    title={'Passport Expiration Page'}
                    description={"If the scan of the page with your name and photo does not include the expiration date, you will need to upload a scan of the page that includes your passport expiration date here."}
                    buttonText={'Upload Expiration Page'}
                    permittedContentTypes={FileUploadSpec.PassportExpirationPage.permittedContentTypes}
                    permittedExtensions={FileUploadSpec.PassportExpirationPage.permittedExtensions}
                    caseId={props.caseId}
                    fileName={FileUploadSpec.PassportExpirationPage.fileName}
                    onUploadSuccess={(response) => {
                      form.setFieldsValue({ passportExpirationUpload: 'uploaded' });
                      return props.onUploadSuccess(response, 'passportExpiration');
                    }}
                    isShowFileName={false}
                  />
                </Form.Item>
              )}
              <Form.Item
                getValueProps={(value) => ({
                  value: !!value
                })}
                style={{ marginTop: '25px' }}
                label="Passport in English?"
                valuePropName="value"
                name={['caseDetails', 'application', 'passportDetails', 'isPassportInLatinCharacters']}
                rules={[
                  {
                    required: true
                  }
                ]}
                validateTrigger={['onChange', 'onBlur']}
                labelCol={{ span: 24 }}
              >
                <Radio.Group
                  style={{ marginLeft: '8px' }}
                  onChange={(e) => {
                    setIsTranslationButtonVisible(e.target.value);
                    form.setFieldsValue({
                      'caseDetails.application.passportDetails.isPassportInLatinCharacters': e.target.value
                    });
                  }}
                  defaultValue={props.applicationPassportDetails?.isPassportInLatinCharacters}
                >
                  <Radio value={false}>No</Radio>
                  <Radio value={true}>Yes</Radio>
                </Radio.Group>
              </Form.Item>
              {isMobile && !isTranslationButtonVisible && (
                <Form.Item
                  name="passportTranslationUpload"
                  rules={[
                    {
                      required: true,
                      message: 'Please upload the passport translation'
                    }
                  ]}
                >
                  <IdCaseFileUploadButtonWithPreviewContainer
                    title={'English Translation of Passport'}
                    description={
                      "If the scan of the page with your name and photo is not in English, you will need to upload an English translation that meets ECFMG's translation requirements."
                    }
                    buttonText={'English Translation of Passport'}
                    permittedContentTypes={FileUploadSpec.PassportTranslation.permittedContentTypes}
                    permittedExtensions={FileUploadSpec.PassportTranslation.permittedExtensions}
                    caseId={props.caseId}
                    fileName={FileUploadSpec.PassportTranslation.fileName}
                    onUploadSuccess={(response) => {
                      form.setFieldsValue({ passportTranslationUpload: 'uploaded' });
                      return props.onUploadSuccess(response, FileUploadSpec.PassportTranslation.fieldName);
                    }}
                    isShowFileName={false}
                  />
                </Form.Item>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}></div>
            {!isMobile && (
              <div style={{ display: 'flex' }}>
                {!isExpirationButtonVisible && (
                  <Form.Item
                    name="passportExpirationUpload"
                    rules={[
                      {
                        required: true,
                        message: 'Please upload the passport expiration page'
                      }
                    ]}
                  >
                    <IdCaseFileUploadButtonWithPreviewContainer
                      title={'Passport Expiration Page'}
                      description={
                        'If the scan of the page with your name and photo does not include the expiration date, you will need to upload a scan of the page that includes your passport expiration date here.'
                      }
                      buttonText={'Upload Expiration Page'}
                      permittedContentTypes={FileUploadSpec.PassportExpirationPage.permittedContentTypes}
                      permittedExtensions={FileUploadSpec.PassportExpirationPage.permittedExtensions}
                      caseId={props.caseId}
                      fileName={FileUploadSpec.PassportExpirationPage.fileName}
                      onUploadSuccess={(response) => {
                        form.setFieldsValue({ passportExpirationUpload: 'uploaded' });
                        return props.onUploadSuccess(response, FileUploadSpec.PassportExpirationPage.fieldName);
                      }}
                      isShowFileName={false}
                    />
                  </Form.Item>
                )}
                {!isTranslationButtonVisible && (
                  <Form.Item
                    name="passportTranslationUpload"
                    // label="Passport Translation"
                    rules={[
                      {
                        required: true,
                        message: 'Please upload the passport translation'
                      }
                    ]}
                    style={{ marginLeft: !isExpirationButtonVisible && !isTranslationButtonVisible ? '136px' : '' }}
                  >
                    <IdCaseFileUploadButtonWithPreviewContainer
                      title={'English Translation of Passport'}
                      description={
                        "If the scan of the page with your name and photo is not in English, you will need to upload an English translation that meets ECFMG's translation requirements."
                      }
                      buttonText={'English Translation of Passport'}
                      permittedContentTypes={FileUploadSpec.PassportTranslation.permittedContentTypes}
                      permittedExtensions={FileUploadSpec.PassportTranslation.permittedExtensions}
                      caseId={props.caseId}
                      fileName={FileUploadSpec.PassportTranslation.fileName}
                      onUploadSuccess={(response) => {
                        form.setFieldsValue({ passportTranslationUpload: 'uploaded' });
                        return props.onUploadSuccess(response, FileUploadSpec.PassportTranslation.fieldName);
                      }}
                      isShowFileName={false}
                    />
                  </Form.Item>
                )}
              </div>
            )}
            <Form.Item
              name="photo"
              style={{ marginTop: '25px' }}
              rules={[
                {
                  required: true,
                  message: 'Please upload the photo'
                }
              ]}
            >
              <IdCaseFileUploadButtonWithPreviewContainer
                title={'Photograph'}
                buttonText={'Upload Photo'}
                permittedContentTypes={FileUploadSpec.Photo.permittedContentTypes}
                permittedExtensions={FileUploadSpec.Photo.permittedExtensions}
                caseId={props.caseId}
                fileName={FileUploadSpec.Photo.fileName}
                onUploadSuccess={(response) => {
                  form.setFieldsValue({ photo: 'uploaded' });
                  return props.onUploadSuccess(response, FileUploadSpec.Photo.fieldName);
                }}
                isShowFileName={false}
                cropperProps={{ aspect: 1 }}
                showDisclaimer={true}
              />
            </Form.Item>
          </div>
          <CertificationByApplicantLegalDisclaimer showAccountEstablishmentAttestation={true} />
        </Form>
        <br />
        <PaymentContainer isDraftMode={true} />
        <br />
        <p><strong>Your credit card will be charged US${props.serviceFee} when you submit this request.</strong></p>
      </div>
      <br />
      <div className={`flex items-center ${isMobile ? 'justify-start' : 'justify-end'} gap-4 w-full pb-5`}>
        <Col>
          <Popconfirm title="Are you sure? Your entries on this page will be deleted." onConfirm={async () => await props.onWithdrawRequest()}>
            <Button danger disabled={props.formLoading}>
              Withdraw Request
            </Button>
          </Popconfirm>
        </Col>

        <Popconfirm title="Are you sure?" onConfirm={onSubmit}>
          <Button type="primary" loading={props.formLoading}>
            Submit Request
          </Button>
        </Popconfirm>
      </div>
    </Content>
  );
};
